/**
 * <s-media-gallery> — scroll-snap product gallery with thumbnail navigation.
 *
 * Markup contract:
 *   <s-media-gallery id="gallery-123">
 *     <div data-gallery-track> ...slides with [data-gallery-slide]... </div>
 *     <button commandfor="gallery-123" command="--go" data-index="0" aria-current="true">...</button>
 *   </s-media-gallery>
 *
 * Thumbnails are buttons using Invoker Commands; the track stays natively swipeable,
 * and the active thumbnail follows whichever slide is in view.
 */
class SMediaGallery extends HTMLElement {
  #ac;
  #observer;
  #current = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;

    // CommandEvent does not bubble, so it is dispatched on this element directly.
    this.addEventListener(
      'command',
      (event) => {
        if (!(event.source instanceof HTMLElement)) return;
        if (event.command === '--step') {
          // Arrow buttons: move one slide, wrapping around at either end.
          const count = track.querySelectorAll('[data-gallery-slide]').length;
          if (!count) return;
          const next = (this.#current + Number(event.source.dataset.step || 1) + count) % count;
          this.#goTo(track, next);
          return;
        }
        if (event.command !== '--go') return;
        const index = Number(event.source.dataset.index || 0);
        this.#goTo(track, index);
      },
      { signal: this.#ac.signal },
    );

    // Jump to the selected variant's image when a shopper picks an option.
    const variantMedia = new Map(
      (this.dataset.variantMedia || '')
        .split(',')
        .filter(Boolean)
        .map((pair) => pair.split(':')),
    );
    const goToMedia = (mediaId, instant) => {
      if (!mediaId) return;
      const all = [...track.querySelectorAll('[data-gallery-slide]')];
      const index = all.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index > -1) this.#goTo(track, index, instant);
    };
    if (variantMedia.size) {
      document.addEventListener(
        'change',
        (event) => {
          const input = event.target;
          if (!(input instanceof HTMLElement)) return;
          const variantId = input.dataset.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
          if (variantId) goToMedia(variantMedia.get(String(variantId)));
        },
        { signal: this.#ac.signal },
      );
      requestAnimationFrame(() => goToMedia(this.dataset.initialMedia, true));
    }

    const slides = [...track.querySelectorAll('[data-gallery-slide]')];
    if (!('IntersectionObserver' in window) || slides.length < 2) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setCurrent(slides.indexOf(entry.target));
          }
        }
      },
      { root: track, threshold: [0.6] },
    );
    slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #goTo(track, index, instant = false) {
    const slide = track.querySelectorAll('[data-gallery-slide]')[index];
    if (!slide) return;
    const reduce = instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the track horizontally so the page never jumps; the track is position:relative so offsetLeft is track-relative.
    const rtl = getComputedStyle(track).direction === 'rtl';
    const offset = rtl
      ? slide.offsetLeft + slide.offsetWidth - track.offsetWidth
      : slide.offsetLeft;
    track.scrollTo({ left: offset, behavior: reduce ? 'auto' : 'smooth' });
    this.#setCurrent(index);
  }

  #setCurrent(index) {
    this.#current = index;
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const active = Number(thumb.dataset.index) === index;
      if (active) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-media-gallery')) {
  customElements.define('s-media-gallery', SMediaGallery);
}
